import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Reveal } from "@/components/Reveal";

marked.setOptions({ gfm: true, breaks: false });

const DOC_PATH_BY_LANG = {
  en: "/legal/privacy.md",
  es: "/legal/privacy.es.md",
  pt: "/legal/privacy.pt.md",
  de: "/legal/privacy.md",
  zh: "/legal/privacy.md",
};

const DATA_MANAGEMENT_PATH_BY_LANG = {
  en: "My Account -> Data Management",
  es: "Mi cuenta -> Gestion de Datos",
  pt: "Minha conta -> Gerenciamento de Dados",
  de: "Mein Konto -> Datenmanagement",
  zh: "我的账户 -> 数据管理",
};

const DELETE_ACCOUNT_PATH_BY_LANG = {
  en: "My Account -> Data Management -> Danger Zone -> Delete all data & reset account",
  es: "Mi cuenta -> Gestion de Datos -> Zona de riesgo -> Eliminar todos los datos y restablecer la cuenta",
  pt: "Minha conta -> Gerenciamento de Dados -> Zona de risco -> Apagar todos os dados e reiniciar a conta",
  de: "Mein Konto -> Datenmanagement -> Gefahrenzone -> Alle Daten loschen und Konto zurucksetzen",
  zh: "我的账户 -> 数据管理 -> 危险区 -> 删除所有数据并重置帐户",
};

const normalizeLang = (value) => (value || "en").toLowerCase().split("-")[0];

const sanitizeHtml = (unsafeHtml) => {
  if (!unsafeHtml || typeof window === "undefined") return unsafeHtml || "";

  const template = window.document.createElement("template");
  template.innerHTML = unsafeHtml;

  template.content
    .querySelectorAll("script, style, iframe, object, embed, link, meta, base")
    .forEach((node) => node.remove());

  template.content.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = (attr.value || "").trim();

      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
        return;
      }

      if (
        (name === "href" || name === "src" || name === "xlink:href") &&
        /^(javascript:|data:text\/html)/i.test(value)
      ) {
        el.removeAttribute(attr.name);
      }
    });

    if (el.tagName === "A") {
      const href = (el.getAttribute("href") || "").trim();
      if (/^https?:\/\//i.test(href)) {
        el.setAttribute("rel", "noopener noreferrer");
        el.setAttribute("target", "_blank");
      }
    }
  });

  return template.innerHTML;
};

const extractLastUpdated = (markdown, lang) => {
  const byLangPatterns = {
    en: /^Last updated:\s*(.+)$/im,
    es: /^Ultima actualizacion:\s*(.+)$/im,
    pt: /^Ultima atualizacao:\s*(.+)$/im,
  };

  const targetPattern = byLangPatterns[lang] || byLangPatterns.en;
  const match = markdown.match(targetPattern)
    || markdown.match(/^Last updated:\s*(.+)$/im)
    || markdown.match(/^Ultima actualizacion:\s*(.+)$/im)
    || markdown.match(/^Ultima atualizacao:\s*(.+)$/im);

  if (!match?.[1]) return "";
  const raw = match[1].trim();
  const iso = raw.match(/[0-9]{4}-[0-9]{2}-[0-9]{2}/)?.[0];
  if (!iso) return raw;

  try {
    const parsed = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) return raw;
    return new Intl.DateTimeFormat(lang, {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    }).format(parsed);
  } catch (_) {
    return raw;
  }
};

const PrivacyPolicyPage = () => {
  const { t, i18n } = useTranslation("site");
  const tk = (k, fb) => t(`privacy.${k}`, fb);
  const lang = normalizeLang(i18n.resolvedLanguage || i18n.language);
  const [contentHtml, setContentHtml] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const sourcePath = useMemo(() => DOC_PATH_BY_LANG[lang] || DOC_PATH_BY_LANG.en, [lang]);
  const dataPath = useMemo(
    () => DATA_MANAGEMENT_PATH_BY_LANG[lang] || DATA_MANAGEMENT_PATH_BY_LANG.en,
    [lang]
  );
  const deletePath = useMemo(
    () => DELETE_ACCOUNT_PATH_BY_LANG[lang] || DELETE_ACCOUNT_PATH_BY_LANG.en,
    [lang]
  );

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(false);

    fetch(sourcePath, { headers: { Accept: "text/markdown,text/plain" } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((markdown) => {
        if (cancelled) return;
        const localized = markdown
          .replace(/\{\{DATA_MANAGEMENT_PATH\}\}/g, dataPath)
          .replace(/\{\{DELETE_ACCOUNT_PATH\}\}/g, deletePath);
        setLastUpdated(extractLastUpdated(localized, lang));
        setContentHtml(sanitizeHtml(marked.parse(localized)));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sourcePath, dataPath, deletePath, lang]);

  return (
    <>
      <section className="relative pb-8">
        <AuroraBackground variant="soft" />
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 pt-8 lg:pt-12">
          <Reveal>
            <p className="eyebrow">{tk("kicker")}</p>
            <h1 className="mt-3 font-serif text-[2.2rem] sm:text-5xl tracking-tight font-light leading-tight">
              {tk("title1")}{" "}<em className="italic text-[var(--aurora-gold)]">{tk("title2")}</em>
            </h1>
            <p className="mt-4 text-white/55 text-sm">
              {lastUpdated
                ? `${t("privacy.updatedLabel", "Last updated")}: ${lastUpdated}`
                : tk("updated")}
            </p>
          </Reveal>
        </div>
      </section>

      <article className="relative py-12">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          {error && (
            <p className="text-white/72 font-light">
              {t(
                "privacy.loadError",
                "We could not load the full privacy policy right now. Please refresh and try again."
              )}
            </p>
          )}
          {loading && !error && (
            <p className="text-white/55 font-light italic">{t("common.loading", "Loading...")}</p>
          )}
          {!loading && !error && (
            <div
              className="prose-aurora text-white/78 font-light leading-relaxed"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          )}
        </div>
      </article>
    </>
  );
};

export default PrivacyPolicyPage;
